import HttpClient from '../http/HttpClient';
import { LEAD_SERVICE_HOST, LEAD_API_PREFIX } from '../constants/RestConstants';

const BASE = `${LEAD_API_PREFIX}/admin/stock-registry`;

function unwrap<T>(response: any): T {
  const body = response?.data ?? response;
  return body?.data ?? body;
}

export interface StockRegistryView {
  id: string;
  companyName: string;
  nseSymbol?: string;
  bseSymbol?: string;
  isin?: string;
  sector?: string;
  industry?: string;
  exchangePrimary?: string;
  marketCapClass?: string;
  status: string;
  dataQualityStatus?: string;
  lastSyncedAt?: string;
  enrichment?: {
    ltp?: number;
    closePrice?: number;
    week52High?: number;
    week52Low?: number;
    syncedAt?: string;
    syncSource?: string;
  };
}

export interface StockRegistryDashboard {
  totalStocks: number;
  nseStocks: number;
  bseStocks: number;
  activeStocks: number;
  suspendedStocks: number;
  delistedStocks: number;
  sectorDistribution: { label: string; count: number }[];
  capClassDistribution: { label: string; count: number }[];
  recentlyUpdated: StockRegistryView[];
  recentImports: {
    id: string;
    fileName: string;
    status: string;
    successCount: number;
    errorCount: number;
    createdDate: string;
  }[];
}

export interface StockImportPreview {
  totalRows: number;
  validRows: number;
  invalidRows: number;
  previewRows: Record<string, unknown>[];
  errors: string[];
  canImport: boolean;
}

export async function getStockRegistryDashboard(): Promise<StockRegistryDashboard> {
  const res = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/dashboard`,
  });
  return unwrap(res);
}

export async function searchStocks(params: {
  q?: string;
  status?: string;
  exchange?: string;
  sector?: string;
  page?: number;
  size?: number;
}) {
  const res = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: BASE,
    params,
  });
  return unwrap(res);
}

export async function previewStockImport(file: File, sourceExchange: string): Promise<StockImportPreview> {
  const form = new FormData();
  form.append('file', file);
  const res = await HttpClient.POST({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/import/preview`,
    params: { sourceExchange },
    data: form,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return unwrap(res);
}

export async function commitStockImport(file: File, sourceExchange: string) {
  const form = new FormData();
  form.append('file', file);
  const res = await HttpClient.POST({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/import`,
    params: { sourceExchange, dryRun: false },
    data: form,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return unwrap(res);
}

export interface StockBreezeSyncResult {
  status: string;
  stock?: StockRegistryView;
  enrichment?: StockRegistryView['enrichment'] & {
    week52High?: number;
    week52Low?: number;
    peRatio?: number;
    pbRatio?: number;
    openPrice?: number;
    highPrice?: number;
    lowPrice?: number;
    volume?: number;
  };
  quotes?: Record<string, unknown>;
  chart?: {
    bars?: Record<string, unknown>[];
    analysis?: Record<string, unknown>;
    stockCode?: string;
    exchangeCode?: string;
    interval?: string;
    breezeError?: string;
  };
  warnings?: string[];
  errors?: string[];
}

export async function syncStockBreeze(stockId: string): Promise<StockBreezeSyncResult> {
  const res = await HttpClient.POST({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/${stockId}/sync-breeze`,
    data: {},
  });
  return unwrap<StockBreezeSyncResult>(res);
}

export async function syncStockBreezeBulk(limit = 20) {
  const res = await HttpClient.POST({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/sync-breeze-bulk`,
    params: { limit },
    data: {},
  });
  return unwrap(res);
}

export async function deactivateStock(id: string) {
  await HttpClient.POST({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/${id}/deactivate`,
    data: {},
  });
}
